// src/pages/Committees/State.jsx
import React, { useEffect, useState } from 'react';
import {
  Table, Button, Container, Alert, Pagination, Modal, Form,
  Badge, Row, Col, Card, Spinner, InputGroup
} from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaFilter, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { committeeAPI, universityAPI } from '../../api/wbuoaApi';

const StateCommittee = () => {
  // State management
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [currentMember, setCurrentMember] = useState({
    sc_id: '',
    university: '',
    name: '',
    designation: '',
    email: '',
    mobile: '',
    portfolio: '',
    show_order: '' // Display order for sorting
  });
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUniversities, setIsLoadingUniversities] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUniversity, setSelectedUniversity] = useState('all');
  const [selectedPortfolio, setSelectedPortfolio] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'show_order', direction: 'asc' });
  const [paginationMeta, setPaginationMeta] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });

  // Portfolio options for dropdown
  const portfolioOptions = [
    'President',
    'Vice President',
    'Secretary',
    'Joint Secretary',
    'Assistant Secretary',
    'Treasurer',
    'Executive Member',
    'Member',
    'Finance Secretary',
    'Academic Coordinator',
    'Research Coordinator',
    'Advisor',
    'Co-Opted Members'
  ];

  // Form label style
  const formLabelStyle = {
    color: '#1a4a2f',
    fontWeight: '500'
  };

  // Fetch members when component mounts or page changes
  useEffect(() => {
    fetchMembers(currentPage);
  }, [currentPage]);

  // Fetch universities once
  useEffect(() => {
    fetchUniversities();
  }, []);

  // Apply filters when members or filter criteria changes
  useEffect(() => {
    applyFilters();
  }, [members, searchTerm, selectedUniversity, selectedPortfolio, sortConfig]);

  // Fetch members with pagination
  const fetchMembers = async (page = 1) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await committeeAPI.listStateCommittee(page, itemsPerPage);
      console.log('State Committee API Response:', response);

      // Handle response structure
      let membersData = [];
      let paginationData = {
        page: 1,
        limit: 10,
        total: 0,
        pages: 1
      };

      if (response && response.data) {
        // Response format: { data: [...], pagination: {...} }
        membersData = response.data;
        if (response.pagination) {
          paginationData = response.pagination;
          // Calculate total pages if not provided
          if (!paginationData.pages && paginationData.total && paginationData.limit) {
            paginationData.pages = Math.ceil(paginationData.total / paginationData.limit);
          }
        }
      } else if (Array.isArray(response)) {
        // Response is directly an array (no pagination)
        membersData = response;
        paginationData = {
          page: 1,
          limit: response.length,
          total: response.length,
          pages: 1
        };
      }

      // Convert show_order to number for proper sorting
      const processedMembers = membersData.map(member => ({
        ...member,
        show_order: member.show_order ? parseInt(member.show_order) : 999 // Default to 999 if no order set
      }));

      setMembers(processedMembers);

      // Update pagination info
      setPaginationMeta(paginationData);
      setTotalPages(paginationData.pages || 1);
      setTotalItems(paginationData.total || processedMembers.length);
      setCurrentPage(paginationData.page || page);

    } catch (err) {
      console.error('Error fetching state committee members:', err);
      setError('Failed to fetch committee members: ' + (err.message || 'Server error'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUniversities = async () => {
    setIsLoadingUniversities(true);
    try {
      const response = await universityAPI.listUniversitiesAll();
      // Format universities from API response - using University field from API
      const universitiesData = Array.isArray(response) ? response : (response.data || []);
      const formattedUniversities = universitiesData.map(univ => ({
        id: univ.U_ID,
        name: univ.University || 'Unknown University',
        value: univ.University || 'Unknown University'
      }));

      // Sort universities alphabetically
      formattedUniversities.sort((a, b) => a.name.localeCompare(b.name));

      setUniversities(formattedUniversities);
    } catch (err) {
      console.error('Failed to fetch universities:', err);
      setError('Failed to fetch universities: ' + (err.message || 'Server error'));
    } finally {
      setIsLoadingUniversities(false);
    }
  };

  // Apply filters, sorting (client-side filtering on current page data)
  const applyFilters = () => {
    let filtered = [...members];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(member =>
        member.name?.toLowerCase().includes(term) ||
        member.designation?.toLowerCase().includes(term) ||
        member.email?.toLowerCase().includes(term) ||
        member.mobile?.toLowerCase().includes(term) ||
        member.portfolio?.toLowerCase().includes(term) ||
        member.university?.toLowerCase().includes(term)
      );
    }

    // Apply university filter
    if (selectedUniversity !== 'all') {
      filtered = filtered.filter(member =>
        member.university === selectedUniversity
      );
    }

    // Apply portfolio filter
    if (selectedPortfolio !== 'all') {
      filtered = filtered.filter(member =>
        member.portfolio === selectedPortfolio
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortConfig.key) {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
      }
      return 0;
    });

    setFilteredMembers(filtered);
  };

  // Handle page change - fetch new page from API
  const handlePageChange = async (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    await fetchMembers(page);
  };

  // Handle sort request
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort />;
    return sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  // Validate form function
  const validateForm = () => {
    const errors = {};
    if (!currentMember.name?.trim()) errors.name = 'Name is required';
    if (!currentMember.university?.trim()) errors.university = 'University is required';
    if (!currentMember.designation?.trim()) errors.designation = 'Designation is required';
    if (!currentMember.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentMember.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!currentMember.mobile?.trim()) errors.mobile = 'Mobile number is required';
    if (!currentMember.portfolio?.trim()) errors.portfolio = 'Portfolio is required';

    // Validate show_order (optional but must be number if provided)
    if (currentMember.show_order && isNaN(currentMember.show_order)) {
      errors.show_order = 'Display order must be a number';
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // Prepare payload with show_order
      const payload = {
        ...currentMember,
        // Ensure show_order is sent as string if it exists, or empty string if not
        show_order: currentMember.show_order || ''
      };

      if (currentMember.sc_id) {
        await committeeAPI.updateStateMember(currentMember.sc_id, payload);
        setSuccess('Member updated successfully');
      } else {
        await committeeAPI.createStateMember(payload);
        setSuccess('Member added successfully');
      }

      // Refresh the list - go back to first page
      setCurrentPage(1);
      await fetchMembers(1);
      setShowModal(false);
      resetForm();

      // Auto-clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error submitting member:', err);
      setError(`Operation failed: ${err.response?.data?.message || err.message || 'Please try again'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setCurrentMember({
      sc_id: '',
      university: '',
      name: '',
      designation: '',
      email: '',
      mobile: '',
      portfolio: '',
      show_order: ''
    });
    setFormErrors({});
  };

  // Handle member deletion
  const handleDelete = async (id) => {
    if (!id) {
      setError('Invalid member ID');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this member? This action cannot be undone.')) {
      return;
    }

    setDeletingId(id);
    setError(null);
    setSuccess(null);

    try {
      await committeeAPI.deleteStateMember(id);
      setSuccess('Member deleted successfully');

      // Check if this was the last item on the current page
      if (members.length === 1 && currentPage > 1) {
        // Go to previous page
        await fetchMembers(currentPage - 1);
      } else {
        // Refresh the current page
        await fetchMembers(currentPage);
      }

      // Auto-clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting member:', err);
      setError(`Failed to delete member: ${err.message || 'Server error'}`);
    } finally {
      setDeletingId(null);
    }
  };

  // Handle edit button click
  const handleEdit = (member) => {
    if (!member?.sc_id) {
      setError('Invalid member data');
      return;
    }

    setCurrentMember({
      sc_id: member.sc_id,
      university: member.university || '',
      name: member.name || '',
      designation: member.designation || '',
      email: member.email || '',
      mobile: member.mobile || '',
      portfolio: member.portfolio || '',
      show_order: member.show_order || ''
    });
    setFormErrors({});
    setShowModal(true);
  };

  // Handle create new member
  const handleCreate = () => {
    resetForm();
    setShowModal(true);
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // For show_order, only allow numbers
    if (name === 'show_order') {
      // Allow empty or numeric values only
      if (value === '' || /^\d*$/.test(value)) {
        setCurrentMember(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setCurrentMember(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedUniversity('all');
    setSelectedPortfolio('all');
    setSortConfig({ key: 'show_order', direction: 'asc' });
  };

  // Generate pagination items
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    let items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    // Previous button
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />
    );

    // First page
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    // Next button
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
    );

    return items;
  };

  // Get stats
  const getStats = () => {
    return {
      total: paginationMeta.total || 0,
      universities: [...new Set(members.map(m => m.university))].filter(u => u).length,
      currentCount: members.length,
      currentPage: currentPage,
      totalPages: totalPages
    };
  };

  const stats = getStats();

  return (
    <Container className="py-4">
      {/* Header Section */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">State Committee Management</h2>
              <p className="text-muted mb-0">
                Manage state committee members and their details. Use display order to control sorting.
              </p>
            </div>
            <Button variant="primary" onClick={handleCreate} disabled={isLoading}>
              <FaPlus className="me-2" />
              Add Member
            </Button>
          </div>

          {/* Search and Filter Section */}
          <Row className="g-3 mb-4">
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search members by name, designation, email, or university..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearchTerm('')}
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={selectedUniversity}
                onChange={(e) => {
                  setSelectedUniversity(e.target.value);
                }}
              >
                <option value="all">All Universities</option>
                {universities.map((university) => (
                  <option key={university.id} value={university.value}>
                    {university.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={selectedPortfolio}
                onChange={(e) => {
                  setSelectedPortfolio(e.target.value);
                }}
              >
                <option value="all">All Portfolios</option>
                {portfolioOptions.map((portfolio, index) => (
                  <option key={index} value={portfolio}>
                    {portfolio}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Button
                variant="outline-secondary"
                onClick={resetFilters}
                className="w-100"
                title="Reset Filters"
              >
                <FaFilter /> Reset
              </Button>
            </Col>
          </Row>

          {/* Stats Cards */}
          <Row className="mt-4">
            <Col md={3}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Total Members</h6>
                      <h4 className="mb-0">{stats.total}</h4>
                    </div>
                    <div className="text-primary">
                      <FaPlus size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Universities</h6>
                      <h4 className="mb-0">{stats.universities}</h4>
                    </div>
                    <div className="text-info">
                      <Badge bg="info">Univ</Badge>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">On Page</h6>
                      <h4 className="mb-0">{stats.currentCount}</h4>
                    </div>
                    <div className="text-dark">
                      <Badge bg="light" text="dark">Now</Badge>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Page</h6>
                      <h4 className="mb-0">{stats.currentPage} / {stats.totalPages}</h4>
                    </div>
                    <div className="text-secondary">
                      <Badge bg="secondary">Page</Badge>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible className="mb-4">
          <Alert.Heading>Success</Alert.Heading>
          <p>{success}</p>
        </Alert>
      )}

      {/* Members Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading committee members...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th
                        style={{ cursor: 'pointer', width: '80px' }}
                        onClick={() => requestSort('show_order')}
                      >
                        <div className="d-flex align-items-center gap-1">
                          Order
                          {getSortIcon('show_order')}
                        </div>
                      </th>
                      <th onClick={() => requestSort('name')} style={{ cursor: 'pointer' }}>
                        <div className="d-flex align-items-center gap-1">
                          Name
                          {getSortIcon('name')}
                        </div>
                      </th>
                      <th onClick={() => requestSort('university')} style={{ cursor: 'pointer' }}>
                        <div className="d-flex align-items-center gap-1">
                          University
                          {getSortIcon('university')}
                        </div>
                      </th>
                      <th onClick={() => requestSort('designation')} style={{ cursor: 'pointer' }}>
                        <div className="d-flex align-items-center gap-1">
                          Designation
                          {getSortIcon('designation')}
                        </div>
                      </th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th onClick={() => requestSort('portfolio')} style={{ cursor: 'pointer' }}>
                        <div className="d-flex align-items-center gap-1">
                          Portfolio
                          {getSortIcon('portfolio')}
                        </div>
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.length > 0 ? (
                      filteredMembers.map(member => (
                        <tr key={member.sc_id}>
                          <td>
                            <Badge bg="light" text="dark" className="fw-normal">
                              {member.show_order || 'N/A'}
                            </Badge>
                          </td>
                          <td>
                            <div className="fw-semibold">{member.name}</div>
                          </td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '150px' }} title={member.university}>
                              {member.university}
                            </div>
                          </td>
                          <td>{member.designation}</td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '180px' }} title={member.email}>
                              {member.email}
                            </div>
                          </td>
                          <td>{member.mobile}</td>
                          <td>
                            <Badge bg="primary" className="text-uppercase">
                              {member.portfolio}
                            </Badge>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleEdit(member)}
                                disabled={deletingId !== null}
                                title="Edit"
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDelete(member.sc_id)}
                                disabled={deletingId !== null}
                                title="Delete"
                              >
                                {deletingId === member.sc_id ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <FaTrash />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center py-5">
                          <div className="py-4">
                            <FaSearch size={48} className="text-muted mb-3" />
                            <h5>No committee members found</h5>
                            <p className="text-muted">
                              {searchTerm || selectedUniversity !== 'all' || selectedPortfolio !== 'all'
                                ? 'Try changing your search or filters'
                                : 'Add your first committee member to get started'}
                            </p>
                            {searchTerm || selectedUniversity !== 'all' || selectedPortfolio !== 'all' ? (
                              <Button variant="outline-primary" onClick={resetFilters}>
                                Reset Filters
                              </Button>
                            ) : (
                              <Button variant="primary" onClick={handleCreate}>
                                <FaPlus className="me-2" /> Add Member
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="p-3 border-top">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="text-muted">
                      Showing page {currentPage} of {totalPages}
                      {totalItems > 0 && ` (Total: ${totalItems} members)`}
                    </div>
                    <Pagination className="mb-0">
                      {renderPagination()}
                    </Pagination>
                  </div>
                </div>
              )}

              {/* Show total items info even when no pagination needed */}
              {totalPages <= 1 && totalItems > 0 && (
                <div className="p-3 border-top">
                  <div className="text-muted text-center">
                    Total: {totalItems} member{totalItems !== 1 ? 's' : ''}
                  </div>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Member Modal */}
      <Modal show={showModal} onHide={() => !isSubmitting && setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{currentMember.sc_id ? 'Edit Committee Member' : 'Add Committee Member'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={formLabelStyle}>University *</Form.Label>
                  {isLoadingUniversities ? (
                    <div className="text-center py-2">
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : (
                    <>
                      <Form.Select
                        name="university"
                        value={currentMember.university}
                        onChange={handleChange}
                        isInvalid={!!formErrors.university}
                        required
                        disabled={isSubmitting}
                      >
                        <option value="">Select University</option>
                        {universities.map((university) => (
                          <option
                            key={university.id}
                            value={university.value}
                          >
                            {university.name}
                          </option>
                        ))}
                        <option key="1001" value="Co-Opted Members">Co-Opted Members</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {formErrors.university}
                      </Form.Control.Feedback>
                    </>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={formLabelStyle}>Display Order</Form.Label>
                  <Form.Control
                    type="text"
                    name="show_order"
                    value={currentMember.show_order}
                    onChange={handleChange}
                    isInvalid={!!formErrors.show_order}
                    placeholder="Enter display order (numbers only)"
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.show_order}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Lower numbers appear first. Members are sorted by this field.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={formLabelStyle}>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={currentMember.name}
                    onChange={handleChange}
                    isInvalid={!!formErrors.name}
                    placeholder="Enter full name"
                    required
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={formLabelStyle}>Designation *</Form.Label>
                  <Form.Control
                    type="text"
                    name="designation"
                    value={currentMember.designation}
                    onChange={handleChange}
                    isInvalid={!!formErrors.designation}
                    placeholder="Enter designation"
                    required
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.designation}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={formLabelStyle}>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={currentMember.email}
                    onChange={handleChange}
                    isInvalid={!!formErrors.email}
                    placeholder="Enter email address"
                    required
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={formLabelStyle}>Mobile *</Form.Label>
                  <Form.Control
                    type="text"
                    name="mobile"
                    value={currentMember.mobile}
                    onChange={handleChange}
                    isInvalid={!!formErrors.mobile}
                    placeholder="Enter mobile number"
                    required
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.mobile}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={formLabelStyle}>Portfolio *</Form.Label>
                  <Form.Select
                    name="portfolio"
                    value={currentMember.portfolio}
                    onChange={handleChange}
                    isInvalid={!!formErrors.portfolio}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select Portfolio</option>
                    {portfolioOptions.map((portfolio, index) => (
                      <option key={index} value={portfolio}>
                        {portfolio}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.portfolio}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <div className="mt-4 pt-2">
                    <div className="alert alert-info mb-0">
                      <small>
                        <strong>Note:</strong> State Committee members typically hold leadership positions at the state level.
                        Ensure accurate portfolio assignment for proper representation.
                      </small>
                    </div>
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button
                variant="secondary"
                onClick={() => setShowModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {currentMember.sc_id ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  currentMember.sc_id ? 'Update Member' : 'Create Member'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default StateCommittee;